import { createContext, useContext, type ReactNode } from 'react';
import { useSceneState } from './useSceneState';

type LastFrameData = { url: string; sourceUrl?: string; materialId: string; width?: number; height?: number } | null;

export type SceneStateContextValue = {
  // States
  scenePreviews: Record<string, string[]>;
  setScenePreviews: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  scenePreviewIndex: Record<string, number>;
  setScenePreviewIndex: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  scenePreviewMaterials: Record<string, any[]>;
  setScenePreviewMaterials: React.Dispatch<React.SetStateAction<Record<string, any[]>>>;
  scenePreviewLoading: Record<string, boolean>;
  setScenePreviewLoading: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  scenePreviewGenerated: Record<string, boolean>;
  setScenePreviewGenerated: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  lastFrameLoading: Record<string, boolean>;
  setLastFrameLoading: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  sceneVideoLoading: Record<string, boolean>;
  setSceneVideoLoading: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  sceneVideoGenerated: Record<string, boolean>;
  setSceneVideoGenerated: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  sceneVideoMaterialId: Record<string, string>;
  setSceneVideoMaterialId: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  sceneVideoError: Record<string, string>;
  setSceneVideoError: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  confirmedFirstFrames: Record<string, string>;
  setConfirmedFirstFrames: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  sceneLastFrames: Record<string, LastFrameData>;
  setSceneLastFrames: React.Dispatch<React.SetStateAction<Record<string, LastFrameData>>>;
  sceneLastFramesRef: React.MutableRefObject<Record<string, LastFrameData>>;
  playedVideos: Set<string>;
  setPlayedVideos: React.Dispatch<React.SetStateAction<Set<string>>>;
  playedNarrations: Set<string>;
  setPlayedNarrations: React.Dispatch<React.SetStateAction<Set<string>>>;
  /** 「すべて開く / すべて閉じる」の合図 (version が進むたびに全シーンカードが collapsed に揃う) */
  sceneCollapseSignal: { version: number; collapsed: boolean };
  // Actions
  swapKeys: (keyA: string, keyB: string) => void;
  deleteKey: (key: string) => void;
  clearAll: () => void;
  setAllScenesCollapsed: (collapsed: boolean) => void;
};

const SceneStateContext = createContext<SceneStateContextValue | null>(null);

export function SceneStateProvider({ children }: { children: ReactNode }) {
  const sceneState = useSceneState();
  return (
    <SceneStateContext.Provider value={sceneState}>
      {children}
    </SceneStateContext.Provider>
  );
}

export function useSceneStateContext(): SceneStateContextValue {
  const ctx = useContext(SceneStateContext);
  if (!ctx) {
    throw new Error('useSceneStateContext must be used within a SceneStateProvider');
  }
  return ctx;
}
